import { useState } from "react";
import * as T from "../../utilities/frontendTypes";
import Button from "../../Components/Button";
import { parseProductCapKey } from "../../utilities/sowCapHelpers";

// Admin editor for a region's SoW product price caps (hard = blocks submission,
// soft = warns) plus the hourly-rate soft cap. Seeded from the region's EFFECTIVE
// caps (program defaults merged with this region's overrides); the server persists
// only values that differ from the default, so a blank field means "no cap".

type CapDraft = Record<string, { soft: string; hard: string }>;

const toInput = (n: number | null): string => (n == null ? "" : String(n));
const toNumOrNull = (s: string): number | null => {
  const t = s.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

const seedDraft = (table: T.SowCapTable): CapDraft => {
  const draft: CapDraft = {};
  for (const [key, cap] of Object.entries(table.products)) {
    draft[key] = { soft: toInput(cap.soft), hard: toInput(cap.hard) };
  }
  return draft;
};

// Group product keys into tier -> category -> [{ key, task }] for rendering.
const groupByTierCategory = (table: T.SowCapTable) => {
  const groups: Record<"tier2" | "tier3", Record<string, { key: string; task: string }[]>> = {
    tier2: {},
    tier3: {},
  };
  for (const key of Object.keys(table.products)) {
    const { tierKey, category, task } = parseProductCapKey(key);
    (groups[tierKey][category] ??= []).push({ key, task });
  }
  return groups;
};

interface SowCapsEditorProps {
  initial: T.SowCapTable;
  onSave: (data: {
    productCapOverrides: Record<string, { soft: number | null; hard: number | null }>;
    hourlyRateSoftCap: number;
  }) => Promise<void> | void;
}

const TIER_LABEL: Record<"tier2" | "tier3", string> = { tier2: "Tier 2", tier3: "Tier 3" };

const SowCapsEditor = ({ initial, onSave }: SowCapsEditorProps) => {
  const [draft, setDraft] = useState<CapDraft>(() => seedDraft(initial));
  const [hourlyCap, setHourlyCap] = useState<string>(String(initial.hourlyRateSoftCap));
  const [saving, setSaving] = useState(false);

  const groups = groupByTierCategory(initial);

  const setCell = (key: string, level: "soft" | "hard", value: string) =>
    setDraft((d) => ({ ...d, [key]: { ...d[key], [level]: value } }));

  const handleSave = async () => {
    const productCapOverrides: Record<string, { soft: number | null; hard: number | null }> = {};
    for (const [key, cell] of Object.entries(draft)) {
      productCapOverrides[key] = { soft: toNumOrNull(cell.soft), hard: toNumOrNull(cell.hard) };
    }
    setSaving(true);
    try {
      await onSave({ productCapOverrides, hourlyRateSoftCap: toNumOrNull(hourlyCap) ?? 0 });
    } finally {
      setSaving(false);
    }
  };

  const numInput =
    "w-24 rounded border border-cMediumGrey/40 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-cSuccess";

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="font-semibold">SoW Cost Caps</h3>
        <p className="text-sm text-text/50 mt-1">
          Per-product price guardrails for this region (pre-tax). A <b>hard</b> cap blocks a
          Service Provider from submitting a SoW; a <b>soft</b> cap lets them submit but warns
          them to justify the cost. Leave a field blank for no cap.
        </p>
      </div>

      {/* Hourly-rate soft cap */}
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium" htmlFor="hourlyCap">
          Hourly rate soft cap ($/hr):
        </label>
        <input
          id="hourlyCap"
          type="number"
          onWheel={(e) => e.currentTarget.blur()}
          min={0}
          className={numInput}
          value={hourlyCap}
          onChange={(e) => setHourlyCap(e.target.value)}
        />
      </div>

      {(["tier2", "tier3"] as const).map((tierKey) => (
        <div key={tierKey}>
          <h4 className="font-semibold mb-2">{TIER_LABEL[tierKey]}</h4>
          {Object.keys(groups[tierKey]).length === 0 && (
            <p className="text-xs text-text/40">No products configured.</p>
          )}
          {Object.entries(groups[tierKey]).map(([category, rows]) => (
            <div key={category} className="mb-4">
              <p className="text-sm font-semibold text-text mb-1">{category}</p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-text/40">
                    <th className="font-normal pb-1 w-1/2"></th>
                    <th className="font-normal pb-1">Soft cap ($)</th>
                    <th className="font-normal pb-1">Hard cap ($)</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ key, task }) => (
                    <tr key={key}>
                      <td className="py-1 pr-2">{task}</td>
                      <td className="py-1 pr-2">
                        <input
                          type="number"
                          onWheel={(e) => e.currentTarget.blur()}
                          min={0}
                          placeholder="none"
                          className={numInput}
                          value={draft[key]?.soft ?? ""}
                          onChange={(e) => setCell(key, "soft", e.target.value)}
                        />
                      </td>
                      <td className="py-1">
                        <input
                          type="number"
                          onWheel={(e) => e.currentTarget.blur()}
                          min={0}
                          placeholder="none"
                          className={numInput}
                          value={draft[key]?.hard ?? ""}
                          onChange={(e) => setCell(key, "hard", e.target.value)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      ))}

      <div className="pb-6">
        <Button buttonText={saving ? "Saving..." : "Save Caps"} actionHandler={handleSave} disabled={saving} />
      </div>
    </div>
  );
};

export default SowCapsEditor;
